import { Box, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { SGR_STATES, SGR_TYPES } from 'lib/consts';
import { SGR_STATES_FILTERS } from 'lib/consts';
import type { RiskPremiumRates } from 'lib/functions/riskPremiumCalculator';
import { formatDate } from 'lib/functions/utils';
import { Document, Warranty } from 'lib/types/models';
import React, { useMemo, useState } from 'react';

import CancelledWarrantyCard from '../CancelledWarrantyCard';
import ClaimedWarrantyCard from '../ClaimedWarrantyCard';
import CreateWarrantyDialog, {
  WarrantyFormData,
} from '../CreateWarrantyDialog';
import DetailedRiskPremiumCalculator from '../DetailedRiskPremiumCalculator';
import LostWarrantyCard from '../LostWarrantyCard';
import PaymentDocument from '../PaymentDocument';
import ConfirmActionDialog from './Components/ConfirmActionDialog';
import GeneralInfo from './Components/GeneralInfo';
import ImportantDates from './Components/ImportantDates';
import Invoicing from './Components/Invoicing';
import MailDialog from './Components/MailDialog';
import PrincipalInfo from './Components/PrincipalInfo';

interface SummaryProps {
  warranty: Warranty & {
    documents?: Document[];
    business?: { primary?: string; secondary?: string };
    tenderUrl?: string;
  };
  setIsEditing: (isEditing: boolean) => void;
  refreshWarranty: () => void;
}

const Summary: React.FC<SummaryProps> = ({
  warranty,
  setIsEditing,
  refreshWarranty,
}) => {
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openClaimDialog, setOpenClaimDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openMarkAsLostDialog, setOpenMarkAsLostDialog] = useState(false);
  const [openMailDialog, setOpenMailDialog] = useState(false);
  const [initialValues, setInitialValues] = useState<WarrantyFormData>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const warrantyType =
    SGR_TYPES.find((type) => type.value === warranty.type)?.label ??
    warranty.type;
  const warrantyStatus = SGR_STATES.find(
    (state) => state.value === warranty.status,
  );

  const [rateControls, setRateControls] = useState<RiskPremiumRates>({
    variableRate: warranty.operationCostVariable,
    fixedRate: warranty.operationCostFixed,
    variableDiscount: warranty.discountRateVariable,
    fixedDiscount: warranty.discountRateFixed,
  });

  const showDocument = useMemo(() => {
    return warranty?.status === 'APPROVED' || warranty?.status === 'ACTIVE';
  }, [warranty.status]);

  const showCalculator = useMemo(() => {
    return (
      warranty?.status === 'QUOTED' ||
      warranty?.status === 'IN_REVIEW' ||
      warranty?.status === 'REJECTED' ||
      warranty?.status === 'LEAD'
    );
  }, [warranty.status]);

  const canEdit = useMemo(() => {
    return (
      warranty.status === 'REJECTED' ||
      warranty.status === 'APPROVED' ||
      warranty.status === 'IN_REVIEW' ||
      warranty.status === 'LEAD'
    );
  }, [warranty.status]);

  const isActive = useMemo(
    () => warranty.status === 'ACTIVE',
    [warranty.status],
  );

  const canMarkAsLost = useMemo(() => {
    const states = SGR_STATES_FILTERS.filter(
      (f) => f.value === 'oportunity' || f.value === 'process',
    ).flatMap((f) => f.states) as readonly string[];
    return states?.includes(warranty.status);
  }, [warranty.status]);

  const menuOptions = useMemo(() => {
    const opts: { label: string; action: () => void }[] = [];
    if (canEdit) {
      opts.push({ label: 'Editar', action: () => setIsEditing(true) });
    }
    if (canMarkAsLost) {
      opts.push({
        label: 'Marcar como perdida',
        action: () => setOpenMarkAsLostDialog(true),
      });
    }
    if (isActive) {
      opts.push({
        label: 'Siniestrar',
        action: () => setOpenClaimDialog(true),
      });
      opts.push({
        label: 'Anular',
        action: () => setOpenCancelDialog(true),
      });
      opts.push({
        label: 'Reemitir',
        action: () => {
          const prefillBusiness = warranty.businessIdentifier
            ? {
                identifier: warranty.businessIdentifier,
                businessName: '',
              }
            : null;
          setInitialValues({
            type: warranty.type,
            quotedAmount: warranty.quotedAmount?.toString() ?? '',
            quotedCurrency: warranty.quotedCurrency as 'UF' | 'USD' | 'CLP',
            startDate: dayjs(),
            endDate: null,
            tenderId: warranty.tenderId ?? '',
            principalRut: warranty.principalRut ?? '',
            unitCode: warranty.unitCode ?? '',
            business: prefillBusiness,
            description: warranty.description ?? '',
            fogape: warranty.fogape ?? undefined,
          });
          setOpenCreateDialog(true);
        },
      });
      opts.push({
        label: 'Enviar Certificado',
        action: () => setOpenMailDialog(true),
      });
    }
    return opts;
  }, [canEdit, isActive, setIsEditing, warranty]);

  const amount = warranty.fixedAmount ?? warranty.quotedAmount;

  return (
    <>
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <GeneralInfo
              warrantyStatus={warrantyStatus}
              warrantyType={warrantyType}
              quotedAmount={warranty.quotedAmount}
              quotedCurrency={warranty.quotedCurrency as 'CLP' | 'USD' | 'UF'}
              tenderId={warranty.tenderId}
              description={warranty.description ?? 'Sin descripción'}
              business={warranty.business}
              tenderUrl={warranty.tenderUrl}
              fogape={warranty.fogape}
              menu={{
                onOpenMenu: handleMenuOpen,
                onCloseMenu: handleMenuClose,
                menuAnchorEl: menuAnchorEl,
                menuOpen: menuOpen,
                menuOptions: menuOptions,
              }}
            />

            <ImportantDates
              quoteDate={formatDate(new Date(warranty.quoteDate), 'dd-mm-yyyy')}
              startDate={formatDate(new Date(warranty.startDate), 'dd-mm-yyyy')}
              endDate={formatDate(new Date(warranty.endDate), 'dd-mm-yyyy')}
            />

            <PrincipalInfo id={warranty.id} />
          </Grid>

          <Grid item xs={12} md={6}>
            {warranty.status === 'CLAIMED' && (
              <Box sx={{ mb: 2 }}>
                <ClaimedWarrantyCard warranty={warranty} />
              </Box>
            )}
            {warranty.status === 'CANCELLED' && (
              <Box sx={{ mb: 2 }}>
                <CancelledWarrantyCard warranty={warranty} />
              </Box>
            )}
            {warranty.status === 'LOST' && (
              <Box sx={{ mb: 2 }}>
                <LostWarrantyCard warranty={warranty} />
              </Box>
            )}
            {showDocument && (
              <Box sx={{ mb: 2 }}>
                <PaymentDocument warranty={warranty} />
              </Box>
            )}
            {isActive && (
              <Box sx={{ mb: 2 }}>
                <Invoicing id={warranty.id} />
              </Box>
            )}

            {showCalculator && (
              <Box>
                <DetailedRiskPremiumCalculator
                  amount={amount}
                  startDate={warranty.startDate}
                  endDate={warranty.endDate}
                  quotedCurrency={warranty.quotedCurrency}
                  type={warranty.type}
                  businessIdentifier={warranty.businessIdentifier}
                  rateControls={rateControls}
                  setRateControls={setRateControls}
                  useConcentrationRates={false}
                  canEditRates={false}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      <ConfirmActionDialog
        type="cancel"
        warrantyRequestId={warranty.id}
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        refreshWarranty={refreshWarranty}
      />
      <ConfirmActionDialog
        type="claim"
        warrantyRequestId={warranty.id}
        open={openClaimDialog}
        onClose={() => setOpenClaimDialog(false)}
        refreshWarranty={refreshWarranty}
      />
      <ConfirmActionDialog
        type="lost"
        warrantyRequestId={warranty.id}
        open={openMarkAsLostDialog}
        onClose={() => setOpenMarkAsLostDialog(false)}
        refreshWarranty={refreshWarranty}
      />
      {openCreateDialog && (
        <CreateWarrantyDialog
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          fetchWarranties={refreshWarranty}
          initialValues={initialValues}
        />
      )}
      <MailDialog
        open={openMailDialog}
        setOpen={setOpenMailDialog}
        warranty={warranty}
      />
    </>
  );
};

export default Summary;
